import { STORAGE_KEYS } from '@/constants';

import type { RestrictedErrorType } from './types/restrictedAreaTypes';

export interface ValidationResult {
  valid: boolean;
  errorType?: RestrictedErrorType | undefined;
  errorMessage?: string | undefined;
}

export function checkConnectivity(): ValidationResult {
  if (!navigator.onLine) {
    return {
      valid: false,
      errorType: 'no_internet',
      errorMessage: 'Internet connection is not available. Please check your network.',
    };
  }
  return { valid: true };
}

export async function checkGPSAvailability(): Promise<ValidationResult> {
  if (!('geolocation' in navigator)) {
    return {
      valid: false,
      errorType: 'gps_off',
      errorMessage: 'Location services are not supported by this browser.',
    };
  }

  try {
    const permission = await navigator.permissions.query({ name: 'geolocation' });
    if (permission.state === 'denied') {
      return {
        valid: false,
        errorType: 'gps_off',
        errorMessage: 'Location is disabled. Please enable location to continue.',
      };
    }
  } catch {
    // permissions.query not supported in all browsers — continue
  }

  return { valid: true };
}

export function checkAuthSession(): {
  hasUser: boolean;
  hasToken: boolean;
  hasPlant: boolean;
  tokenExpired: boolean;
} {
  const userRaw = localStorage.getItem(STORAGE_KEYS.user);
  const tokenRaw = localStorage.getItem(STORAGE_KEYS.authToken);
  const plantRaw = localStorage.getItem(STORAGE_KEYS.plant);

  let tokenExpired = false;
  if (tokenRaw) {
    try {
      const parsed: unknown = JSON.parse(tokenRaw);
      // Simple expiry heuristic — if the token is a JWT, check exp claim
      if (typeof parsed === 'string' && parsed.includes('.')) {
        const payload: unknown = JSON.parse(atob(parsed.split('.')[1] ?? ''));
        if (typeof payload === 'object' && payload !== null && 'exp' in payload) {
          const exp = (payload as { exp: number }).exp;
          tokenExpired = Date.now() > exp * 1000;
        }
      }
    } catch {
      // Non-JWT token or parse failure — assume valid
    }
  }

  return {
    hasUser: userRaw !== null,
    hasToken: tokenRaw !== null,
    hasPlant: plantRaw !== null,
    tokenExpired,
  };
}
export function checkConfigAvailable(): ValidationResult {
  const configRaw = localStorage.getItem(STORAGE_KEYS.globalConfig);
  if (!configRaw) {
    return {
      valid: false,
      errorType: 'config_missing',
      errorMessage: 'Application configuration is missing. Please try again.',
    };
  }
  return { valid: true };
}
export async function validateEnvironment(): Promise<ValidationResult> {
  // 1. Connectivity
  const connectivity = checkConnectivity();
  if (!connectivity.valid) return connectivity;

  // 2. GPS
  const gps = await checkGPSAvailability();
  if (!gps.valid) return gps;

  // 3. Config
  const config = checkConfigAvailable();
  if (!config.valid) return config;

  return { valid: true };
}
export function getCurrentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    });
  });
}
